/**
 * The original's own **33-entry aircraft-code table**, mapped onto this port's
 * fleet keys — and the **25-entry ace order**, which needs no mapping at all.
 *
 * These are the two tables `vcr.c` validates `SIM.IN`'s bytes against (the
 * aircraft remap at `0x4279:0` and the ace table at `0x4274:0`,
 * docs/formats/missions-campaign.md § `SIM.IN`), and the same two
 * `HISTORIC.DAT` indexes into. The code order is `MBACEDAT.DOC` Table 2, which
 * groups Allied types first and then German ones; `AIRCRAFT_ROWS` orders the
 * identical 33 aeroplanes by service date instead, so the two are the same set
 * in a different order and the mapping below has to be written out rather than
 * assumed to be the identity.
 *
 * **The expansion renumbered this table.** `HISTORIC.DAT`'s two revisions
 * differ by exactly the shift that inserting Nieuport 11 (`0x0A`) and Nieuport
 * 28 (`0x0B`) into the Allied block causes — every German code two higher in
 * the expansion's file (`packages/formats/test/historic-dat.test.ts`). This
 * table is the **expansion** numbering, which is what our install's loose
 * `ACE.DAT` and `HISTORIC.DAT` use, and what the 33-aeroplane fleet needs.
 */

import { AIRCRAFT_KEYS, type AircraftKey } from "../flight/aircraft-table.ts";

/**
 * Index = the file's aircraft-code byte. `MBACEDAT.DOC` Table 2's own names are
 * in docs/formats/historic-dat.md beside each row.
 */
export const AIRCRAFT_CODE_KEYS: readonly AircraftKey[] = [
  "MORANE", // 0x00 Morane-Saulnier Type N, Bullet
  "AIRCO", // 0x01 Dehaviland DH-2
  "NIEUPORT", // 0x02 Nieuport 17
  "SOPPUP", // 0x03 Sopwith Pup
  "SOPTRI", // 0x04 Sopwith Triplane
  "SPAD_7", // 0x05 SPAD VII
  "SE5", // 0x06 Se-5A
  "CAMEL", // 0x07 Sopwith Camel
  "SPAD_13", // 0x08 SPAD XIII
  "SNIPE", // 0x09 Sopwith Snipe
  "NIEU11", // 0x0A Nieuport 11
  "NIEU28", // 0x0B Nieuport 28
  "RE8", // 0x0C R.E. 8
  "FE2", // 0x0D Fe-2b
  "BE2", // 0x0E B.E. 2c
  "BRISTOL", // 0x0F Bristol F.2B, Brisfit
  "DH4", // 0x10 Dehaviland DH-4
  "HANDLEY", // 0x11 Handley-Paige 0/400
  "FOKE3", // 0x12 Fokker E-III, Eindekker
  "ALBA_D2", // 0x13 Albatros D-II
  "ALBA_D3", // 0x14 Albatros D-III
  "FOKDR1", // 0x15 Fokker Dr-I
  "PFALZ", // 0x16 Pfalz D-III
  "ALBA_D5", // 0x17 Albatros D-Va
  "FOKD7", // 0x18 Fokker D-VII
  "FOKD8", // 0x19 Fokker D-VIII
  "HALBD2", // 0x1A Halberstadt D-II
  "SIEMENS", // 0x1B Siemens-Schuckert Werks (SSW) D-III
  "AVIATIK", // 0x1C Aviatik C-I
  "ROLAND", // 0x1D Roland C-II
  "RUMPLER", // 0x1E Rumpler C-IV
  "GOTHA", // 0x1F Gotha Go-IV
  "JUNKER", // 0x20 Junkers Ju-1
];

/** Resolves a file byte to a fleet key, or throws — the same discipline `vcr.c` applies. */
export function aircraftForCode(code: number): AircraftKey {
  const key = AIRCRAFT_CODE_KEYS[code];
  if (!key) throw new Error(`aircraft code ${code} is outside the ${AIRCRAFT_CODE_KEYS.length}-entry table`);
  return key;
}

/** The inverse, for a bake stage or a test that wants to go back the other way. */
export function codeForAircraft(key: AircraftKey): number {
  const code = AIRCRAFT_CODE_KEYS.indexOf(key);
  if (code < 0) throw new Error(`${key} has no aircraft code`);
  return code;
}

/** The table is a bijection onto the fleet: same 33 aeroplanes, different order. */
export const AIRCRAFT_CODE_COUNT = AIRCRAFT_KEYS.length;
